import React, { useEffect, useState } from 'react';
import { GOOGLE_FAVICON_HOST_RE, buildLogoSources } from '../../../utils/urlMetadata';

/**
 * Unified company-logo hook for any surface: resolves the ordered source chain
 * from the directly-provided `logo`/`websiteUrl` and drives the
 * render-fallback state machine (advance on <img> error; drop a Google favicon
 * that loads as the generic globe at or under `genericFaviconMaxSizePx`) in one
 * place. No network fetches: when the surface has no logo/website, callers
 * render their default first-letter badge.
 */
export function useCompanyLogo(params: { logo?: string; websiteUrl?: string; genericFaviconMaxSizePx?: number }): {
    currentSource: string | undefined;
    handleError: () => void;
    handleLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
} {
    const { logo, websiteUrl, genericFaviconMaxSizePx = 16 } = params;

    const sources = buildLogoSources(logo, websiteUrl);

    // Render-fallback state machine over the resolved sources: advance on
    // <img> error, reset to the first source whenever the list changes by
    // content, and treat a small Google favicon load as a miss.
    const [index, setIndex] = useState(0);

    const sourcesKey = sources.join('|');
    useEffect(() => {
        setIndex(0);
    }, [sourcesKey]);

    const currentSource = sources[index];
    const advance = () => setIndex((i) => i + 1);

    const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        // Google's favicon service returns a 16x16 generic globe when it has no
        // real favicon for the domain. That loads successfully, so onError never
        // fires — detect the tell-tale small globe and fall through to the next
        // source (ultimately the first-letter badge) instead of showing it.
        const img = e.currentTarget;
        const isGoogleFavicon = GOOGLE_FAVICON_HOST_RE.test(currentSource ?? '');
        if (isGoogleFavicon && img.naturalWidth > 0 && img.naturalWidth <= genericFaviconMaxSizePx) {
            advance();
        }
    };

    return { currentSource, handleError: advance, handleLoad };
}
